import { Grid, Layout, theme } from "antd";
import type React from "react";
import { useState } from "react";
import { ActionsProvider } from "@/contexts/actions-context";
import { PageTitleProvider } from "@/contexts/page-title-context";
import { WizardsProvider } from "@/contexts/wizard-context";
import { CustomDrawer } from "./custom-drawer";
import { PageHeader } from "./page-header";
import { SideNav } from "./side-nav";

const { Content, Sider } = Layout;

function WorkspaceLayout({ children }: { children: React.ReactNode }) {
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;
	const [collapsed, setCollapsed] = useState(false);
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const {
		token: { colorBgLayout, colorBgContainer },
	} = theme.useToken();

	const openMobileNav = () => setMobileNavOpen(true);

	return (
		<PageTitleProvider>
			<ActionsProvider>
				<WizardsProvider>
					<Layout style={{ height: "100vh", overflow: "hidden" }}>
						<Layout>
							<Sider
								theme="light"
								collapsible
								collapsed={collapsed}
								onCollapse={setCollapsed}
								collapsedWidth={isMobile ? 0 : 64}
								width={240}
								breakpoint="md"
								trigger={null}
								style={{
									overflow: "hidden",
									background: colorBgContainer,
									boxShadow: "1px 0 0 0 rgba(0,0,0,0.06)",
								}}
							>
								<SideNav
									collapsed={collapsed}
									onToggleCollapse={() => setCollapsed((c) => !c)}
								/>
							</Sider>

							<CustomDrawer
								open={mobileNavOpen}
								onClose={() => setMobileNavOpen(false)}
								placement="bottom"
								// Explicit height: "full" isn't a valid antd size, so without this the bottom
								// drawer sizes to content and the flex children have no height to scroll against.
								height="85dvh"
								title="StratoNext"
								// Flex-column body with a bounded height so SideNav (flex:1) fills it and its
								// inner list owns the vertical scroll.
								styles={{
									body: {
										padding: 20,
										display: "flex",
										flexDirection: "column",
										minHeight: 0,
										overflow: "hidden",
									},
								}}
							>
								<SideNav
									collapsed={false}
									onNavigate={() => setMobileNavOpen(false)}
									fullHeight={false}
								/>
							</CustomDrawer>

							<Layout style={{ overflow: "hidden" }}>
								<PageHeader onMenuClick={openMobileNav} />
								<Content
									style={{
										background: colorBgLayout,
										paddingTop: 4,
										padding: isMobile ? 12 : 20,
										overflowY: "auto",
										flex: 1,
										minHeight: 0,
									}}
								>
									{children}
								</Content>
							</Layout>
						</Layout>
					</Layout>
				</WizardsProvider>
			</ActionsProvider>
		</PageTitleProvider>
	);
}

export default WorkspaceLayout;
